import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import ReactTooltip from 'react-tooltip';
// import * as Sentry from '@sentry/react';
import { useAssetMetadataMutation } from 'src/api/mutations/assetMutations';
import { useAssetSchemaQuery } from 'src/api/queries/schemesQueries';
import { queryNames } from 'src/api/utils/queryNames';
import { useAlerts } from 'src/common/AlertManager';
import { Pencil } from 'src/common/icons';
import { Asset } from 'src/types/TAssets';

import styles from '../Modal.module.scss';
import MetadataForm from 'src/components/MetadataForm';

import AssetMetadataSetup from './AssetMetadataSetup';
import { isEmpty, set } from 'lodash';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import useRoleManager from 'src/hooks/useRoleManager';
import { useWritesPaused } from 'src/hooks/useWritesPaused';
import { PERMISSIONS } from 'src/common/roles';
import { Dropdown, Loader } from 'src/components';
import { ISubmitEvent } from '@rjsf/core';

type TAssetMetadata = {
  asset: Asset;
  schemas: any;
};

// AI-generated tags live in this internal field as a comma-separated string.
// Unlike other _filespin* fields, users may edit it to correct bad tags.
const AUTOTAG_LABELS_KEY = '_filespin_autotag_labels';
const VISIBLE_TAGS_COUNT = 5;

// Text extracted from the image (OCR) by the IMAGE_ANALYSIS addon. Like AI
// tags, it is a user-editable internal field so users can correct bad OCR.
const OCR_TEXT_KEY = '_filespin_ocr_text';

// Where the asset came from before it was here. Written by whichever producer
// brought it in — today the Cloudinary/ImageKit migration, later the Adobe
// plugin and Teleport recording the path it had on the designer's own machine.
//
// Read-only on purpose: unlike AI tags and OCR text, these are not a guess the
// user might want to correct. They are the record of where the file came from,
// which is what makes a bad import undoable. The backend enforces that —
// RESERVED_METADATA_FIELDS treats any `_filespin*` key as internal and drops a
// customer's write of it — so there is no edit control here to promise
// otherwise.
const SOURCE_SYSTEM_KEY = '_filespin_source_system';
const SOURCE_PATH_KEY = '_filespin_source_path';

// The value stored is a machine name. Anything not listed is shown as written,
// so a producer we do not know about yet still displays something truthful
// rather than nothing.
const SOURCE_SYSTEM_LABELS: Record<string, string> = {
  cloudinary: 'Cloudinary',
  imagekit: 'ImageKit',
  webserver: 'A web server',
  teleport: 'FileSpin Teleport',
  'adobe-photoshop': 'Adobe Photoshop',
  'adobe-indesign': 'Adobe InDesign',
  'adobe-illustrator': 'Adobe Illustrator',
  // AI agents uploading through the MCP server (the MCP client's name)
  'claude-code': 'Claude Code',
  'claude-ai': 'Claude',
  cursor: 'Cursor',
  codex: 'Codex',
  vscode: 'VS Code',
  mcp: 'AI agent',
  'filespin-agent': 'FileSpin AI agent',
  'upload-link': 'Upload link',
};

type AssetSourceProps = {
  system: string;
  path: string;
};

const AssetSource: React.FC<AssetSourceProps> = ({ system, path }) => (
  <div className={styles.extractedTextSection}>
    <div className={styles.extractedTextHeader}>
      <div className={styles.title}>Where this came from</div>
    </div>
    {system && (
      <div className={styles.metaField}>
        <p>Uploaded from</p>
        <div>{SOURCE_SYSTEM_LABELS[system] || system}</div>
      </div>
    )}
    {path && (
      <div className={styles.metaField}>
        <p>Its path there</p>
        {/* Wraps rather than truncates: a folder path is the whole point of
            the field, and an elided one cannot be checked against the old
            provider or pasted into a search. */}
        <div className={styles.sourcePath}>{path}</div>
      </div>
    )}
  </div>
);

const parseAutotagLabels = (value: unknown): string[] =>
  typeof value === 'string'
    ? value
      .split(',')
      .map((label) => label.trim())
      .filter(Boolean)
    : [];

type AiTagsProps = {
  labels: string[];
  canEdit: boolean;
  onSave: (labels: string[]) => void;
};

const AiTags: React.FC<AiTagsProps> = ({ labels, canEdit, onSave }) => {
  const [showAll, setShowAll] = useState(false);
  const [newTag, setNewTag] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showAll) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowAll(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showAll]);

  const removeTag = (tag: string) => onSave(labels.filter((label) => label !== tag));

  const addTag = () => {
    const tag = newTag.trim();
    if (!tag) return;
    setNewTag('');
    if (labels.includes(tag)) return;
    onSave([...labels, tag]);
  };

  const renderChip = (tag: string) => (
    <span className={styles.aiTag} key={tag}>
      {tag}
      {canEdit && (
        <button
          type="button"
          className={styles.aiTagRemove}
          onClick={() => removeTag(tag)}
          aria-label={`Remove tag ${tag}`}
        >
          ×
        </button>
      )}
    </span>
  );

  const hiddenCount = labels.length - VISIBLE_TAGS_COUNT;

  return (
    <div className={styles.aiTagsSection} ref={wrapperRef}>
      <div className={styles.title}>AI Tags</div>
      <div className={styles.aiTags}>
        {labels.slice(0, VISIBLE_TAGS_COUNT).map(renderChip)}
        {hiddenCount > 0 && (
          <button
            type="button"
            className={styles.moreTags}
            onClick={() => setShowAll((old) => !old)}
            aria-expanded={showAll}
          >
            +{hiddenCount}
          </button>
        )}
        {canEdit && (
          <input
            className={styles.aiTagInput}
            type="text"
            value={newTag}
            placeholder="Add tag"
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag();
              }
            }}
          />
        )}
        {showAll && <div className={styles.tagsOverlay}>{labels.map(renderChip)}</div>}
      </div>
    </div>
  );
};

type ExtractedTextProps = {
  text: string;
  canEdit: boolean;
  onSave: (text: string) => void;
};

const ExtractedText: React.FC<ExtractedTextProps> = ({ text, canEdit, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);

  useEffect(() => {
    setDraft(text);
  }, [text]);

  const save = () => {
    setEditing(false);
    if (draft.trim() === text.trim()) return;
    onSave(draft.trim());
  };

  const cancel = () => {
    setDraft(text);
    setEditing(false);
  };

  return (
    <div className={styles.extractedTextSection}>
      <div className={styles.extractedTextHeader}>
        <div className={styles.title}>Extracted Text</div>
        {canEdit && !editing && (
          <button
            type="button"
            className={styles.extractedTextEdit}
            onClick={() => setEditing(true)}
            aria-label="Edit extracted text"
          >
            <Pencil />
          </button>
        )}
      </div>
      {editing ? (
        <div className={styles.extractedTextEditor}>
          <textarea
            className={styles.extractedTextArea}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={5}
            autoFocus
          />
          <div className={styles.extractedTextActions}>
            <button type="button" className={styles.extractedTextCancel} onClick={cancel}>
              Cancel
            </button>
            <button type="button" className={styles.extractedTextSave} onClick={save}>
              Save
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.extractedTextBody}>{text}</div>
      )}
    </div>
  );
};

const renderMetadata = (metadata: any, canEdit: boolean, onEdit: () => void) => {
  const userEntries = Object.entries(metadata).filter(
    ([k]) => k !== 'data_schema_id' && !k.startsWith('_filespin'),
  );

  return (
    <>
      <div className={styles.title}>Data</div>
      {userEntries.length === 0 ? (
        <div className={styles.noMetadata}>
          No metadata added.{' '}
          {canEdit ? (
            <>
              <button type="button" className={styles.noMetadataEdit} onClick={onEdit}>
                Edit
              </button>{' '}
              to add or use AI Agent
            </>
          ) : (
            'Use AI Agent to add.'
          )}
        </div>
      ) : (
        userEntries.map(([k, v]) => (
          <div className={styles.metaField} key={k}>
            <p>{k}</p>
            <div>{String(v)}</div>
          </div>
        ))
      )}
    </>
  );
};

type RenderEditMetadataProps = {
  schema?: any;
  metadata: any;
  onSubmit: ((e: ISubmitEvent<any>, nativeEvent: React.FormEvent<HTMLFormElement>) => any) | undefined;
  onCancel: () => void;
};

const RenderEditMetadata: React.FC<RenderEditMetadataProps> = ({ schema, metadata, onSubmit, onCancel }) => {
  return <MetadataForm schema={schema} metadata={metadata} onSubmit={onSubmit} onCancel={onCancel} />;
};

export const AssetMetadata: React.FC<TAssetMetadata> = ({ asset, schemas }) => {
  // const metadata = asset?.data ?? {}
  const data_schema_id = asset.data_schema_id ?? 0;

  const metadata =
    data_schema_id === 0
      ? Object.fromEntries(
        Object.entries(asset?.data ?? {})
          .filter(([k]) => k !== 'data_schema_id')
          .map(([k, v]) => [k, String(v)]),
      )
      : Object.fromEntries(Object.entries(asset?.data ?? {}).filter(([k]) => k !== 'data_schema_id'));

  const [metadataEditMode, setMetadataEditMode] = useState(false);
  const [schema, setSchema] = useState<any>();

  const [selectedSchemaId, setSelectedSchemaId] = useState(asset?.data_schema_id ?? 0);
  const [selectSchemaOptions, setSelectSchemaOptions] = useState<any>([]);
  const [schemaIdDataMapping, setSchemaIdDataMapping] = useState<any>({});
  const [schemaKeyValueMapping, setSchemaKeyValueMapping] = useState<any>({});

  const queryClient = useQueryClient();
  const { showSuccess, showAlert } = useAlerts();


  const { getErrorMessage } = useErrorMessages();
  const { isUser, isCreator, hasPermission } = useRoleManager();
  // A paused account refuses every metadata write (503), so the panel turns
  // read-only rather than offering an edit that can only fail.
  const { paused: writesPaused } = useWritesPaused();
  const canEditAsset = hasPermission(PERMISSIONS.EDIT_ASSET) && !writesPaused;
  const metadataMutation = useAssetMetadataMutation(asset.id, {
    onError: (error, newData, context: any) => {
      setSelectedSchemaId(asset?.data_schema_id ? asset?.data_schema_id : 0);
      setSchema(schemaIdDataMapping?.[asset?.data_schema_id]?.schema);

      queryClient.setQueryData([queryNames.asset, asset.id], context.previousData);
      // TODO: Handle error, show feedback
      // Sentry.captureMessage(
      //   `Failed metadata mutation for asset ${asset.id} with error: ${error.message}`,
      //   Sentry.Severity.Error,
      // );

      showAlert(getErrorMessage('system.errorMessages.unknownError'));
    },
    onMutate: (updated_data) => {
      
      let data = { ...updated_data.data };
      setSelectedSchemaId(updated_data?.data_schema_id)
  
      setMetadataEditMode(false);
      const previousData = queryClient.getQueryData<Asset>([queryNames.asset, asset.id]);
      queryClient.setQueryData<Asset | undefined>([queryNames.asset, asset.id], (old) => {
    
        if (typeof old !== 'undefined') {
          old.data = data;
          old.data_schema_id=updated_data?.data_schema_id
        }
        return old;
      });
     
      return { previousData };
    },
    onSuccess: (updated_data) => {
      showSuccess('Asset metadata updated sucessfully');
    }
  });


  // query the metadata
  // const {
  //   data: schemaData,
  //   error: schemaError,
  //   isLoading: isSchemaLoading,
  // } = useAssetSchemaQuery(
  //   data_schema_id,
  //   {
  //     staleTime: 1000 * 60 * 5,
  //     refetchOnMount: true,
  //   },
  //   { enabled: !!data_schema_id },
  // );

  const sortByKeyAscending = (options: Array<{ key: string | number; value: string }>) => {
    return options.sort((a, b) => Number(a.key) - Number(b.key));
  };

  useEffect(() => {
    // setSelectedSchemaId(asset?.data_schema_id);
    const keyValueMapping: any = {};
    const idSchemaMapping: any = {};

    if (!schemas) return;
    let options = schemas?.map((schema: any) => {
      keyValueMapping[schema.id] = schema.name.en;
      idSchemaMapping[schema.id] = schema;
      return { value: schema.name.en + ` (ID:${schema.id})`, key: schema.id };
    });

    options=sortByKeyAscending(options)
    

    setSchemaKeyValueMapping(keyValueMapping);
    setSchemaIdDataMapping(idSchemaMapping);
    setSchema(idSchemaMapping[data_schema_id]?.schema ?? {})
    setSelectSchemaOptions(options);
  }, [schemas, asset]);

  // useEffect(() => {
  //   if (!schemaData) return;
  //   console.log(schemaData)
  //   const { $schema, ...data } = schemaData?.schema ?? { schema: {} };
    
  //   setSchema(data);
  // }, [schemaData]);

  const submitMetadata = async (schemaObject: any) => {
    
    await metadataMutation.mutateAsync({
      data: schemaObject?.formData,
      mode: 'APPEND',
      data_schema_id: Number(selectedSchemaId),
    });
  };

  const submitSchema = async (schemaObject: any, schemaId: number) => {
    await metadataMutation.mutateAsync({
      data: schemaObject?.formData,
      mode: 'REPLACE',
      data_schema_id: schemaId,
    });
  };

  const autotagLabels = parseAutotagLabels(asset?.data?.[AUTOTAG_LABELS_KEY]);

  // Submit the full data blob (not just the tags) so the optimistic update in
  // onMutate — which replaces asset.data with the submitted object — stays correct.
  const saveAutotagLabels = (labels: string[]) => {
    metadataMutation.mutate({
      data: { ...metadata, [AUTOTAG_LABELS_KEY]: labels.join(', ') },
      mode: 'APPEND',
      data_schema_id: Number(selectedSchemaId),
    });
  };

  // AI tags are backend-managed and schema-independent, so they render in
  // their own card above all schema-driven sections, in every branch.
  const aiTagsBlock =
    autotagLabels.length > 0 ? (
      <div className={`${styles.metadataWrapper} ${styles.aiTagsWrapper}`}>
        {/* Tag editing goes through the shared metadata mutation, whose
            optimistic update would close the edit form and drop unsaved
            input — so tags are read-only while the form is open. */}
        <AiTags
          labels={autotagLabels}
          canEdit={canEditAsset && !metadataEditMode}
          onSave={saveAutotagLabels}
        />
      </div>
    ) : null;

  const ocrText =
    typeof asset?.data?.[OCR_TEXT_KEY] === 'string' ? (asset.data[OCR_TEXT_KEY] as string) : '';

  // Submit the full data blob (not just the text) so the optimistic update in
  // onMutate stays correct, mirroring saveAutotagLabels.
  const saveOcrText = (text: string) => {
    metadataMutation.mutate({
      data: { ...metadata, [OCR_TEXT_KEY]: text },
      mode: 'APPEND',
      data_schema_id: Number(selectedSchemaId),
    });
  };

  // Extracted (OCR) text is backend-managed and schema-independent, like AI
  // tags, so it renders in its own card above the schema-driven sections.
  const extractedTextBlock =
    ocrText.trim().length > 0 ? (
      <div className={`${styles.metadataWrapper} ${styles.aiTagsWrapper}`}>
        <ExtractedText
          text={ocrText}
          canEdit={canEditAsset && !metadataEditMode}
          onSave={saveOcrText}
        />
      </div>
    ) : null;

  // Provenance is backend-managed and schema-independent, like AI tags and OCR
  // text, so it renders in its own card in every branch. Without this it was
  // stored and searchable but invisible — the `_filespin` prefix that makes it
  // read-only also makes `renderMetadata` filter it out.
  //
  // It sits BELOW the schema, unlike the other two cards. AI tags and extracted
  // text are about the picture, so they belong next to the metadata someone is
  // reading or editing; where a file came from is a footnote about the file, and
  // putting it first pushed the schema — the thing people open this panel for —
  // down the page.
  const sourceSystem =
    typeof asset?.data?.[SOURCE_SYSTEM_KEY] === 'string' ? (asset.data[SOURCE_SYSTEM_KEY] as string) : '';
  const sourcePath =
    typeof asset?.data?.[SOURCE_PATH_KEY] === 'string' ? (asset.data[SOURCE_PATH_KEY] as string) : '';

  const sourceBlock =
    sourceSystem || sourcePath ? (
      <div className={`${styles.metadataWrapper} ${styles.sourceWrapper}`}>
        <AssetSource system={sourceSystem} path={sourcePath} />
      </div>
    ) : null;

  if (!schema && isEmpty(metadata)) {
    return (
      <>
        {aiTagsBlock}
        {extractedTextBlock}
        <div className={styles.metadataWrapper}>
          <AssetMetadataSetup onSubmitSchema={submitSchema} asset={asset} schemasData={schemas} />
        </div>
        {sourceBlock}
      </>
    );
  }
  const renderDropdownValue = (value: string) => {
    if (!value || value.length <= 28) {
      return value;
    }
    return `${value.slice(0, 25)}...`;
  };

  return (
    <>
      {aiTagsBlock}
      {extractedTextBlock}
      {selectSchemaOptions?.length > 0 && schemas?.length > 0 ? (
        <div className={styles.metadataWrapper}>
          <div className={styles.title}>
            Schema
            {!isUser && !writesPaused && (
              <button
                className={styles.edit}
                onClick={() => setMetadataEditMode(true)}
                data-tip="Edit asset metadata"
              >
                <Pencil />
                <ReactTooltip effect="solid" />
              </button>
            )}
          </div>
          <Dropdown
            className={styles.dropD}
            isMultiple={false}
            disabled={writesPaused}
            options={selectSchemaOptions}
            onClick={(key) => {
              setSelectedSchemaId(key);
              setSchema(schemaIdDataMapping[Number(key)]?.schema);
              setMetadataEditMode(true);
            }}
          >
            {selectSchemaOptions?.length > 0 ? (
              `${renderDropdownValue(schemaKeyValueMapping[selectedSchemaId])} (ID: ${selectedSchemaId})`
            ) : (
              <br />
            )}
          </Dropdown>
          {!metadataEditMode && renderMetadata(metadata, canEditAsset, () => setMetadataEditMode(true))}
          {/* {metadataEditMode && renderEditMetadata(schema, metadata, submitMetadata, () => setMetadataEditMode(false))} */}
          {metadataEditMode && (
            <RenderEditMetadata
              schema={schema}
              metadata={metadata}
              onSubmit={submitMetadata}
              onCancel={() => {
                setMetadataEditMode(false);
                // setSelectedSchemaId(asset.data_schema_id)
                // setSchema(schemaIdDataMapping[asset?.data_schema_id]?.schema)
              }}
            />
          )}
        </div>
      ) : (
        <div style={{ textAlign: 'center', marginTop: '250px' }}>No MetaData Available!</div>
      )}
      {sourceBlock}
    </>
  );
};
